import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { MAX_DOCUMENT_FILE_SIZE_ALLOWED, MAX_DOCUMENT_SIZE_IN_MB } from 'common/constants/documents';
import validFileTypes from 'components/Documents/validFileTypes';
import {
  Button,
  Dialog,
  DialogActionFooter,
  DialogActions,
  DialogCloseButton,
  DialogCloseIcon,
  DialogHeader,
  DialogTitle,
  FileTypeIcon,
  FileUploadDropzone,
  OverflowTooltip,
  ScrollHiddenItemsHint,
} from 'components/ui';
import { useScrollHiddenItemCounts } from 'components/ui/hooks';
import { getFirmDocumentReferenceFileName } from 'pages/Documents/FirmDocuments/utilities';
import { useGetMeasurementDatesByCompany } from 'services/hooks/companies';
import {
  FIRM_LEVEL_DOCUMENTS_ID,
  FIRM_LEVEL_DOCUMENTS_QUERY_VALUE,
  useFirmDocumentFoldersQuery,
  useFirmDocumentMeasurementDateDocumentsQuery,
} from 'services/hooks/documents';
import { shortDate } from 'utilities';
import FirmDocumentDialogSearchableSelect from '../FirmDocumentDialogSearchableSelect';
import type { FirmDocumentDialogSearchableSelectOption } from '../FirmDocumentDialogSearchableSelect';
import {
  FirmDocumentUploadDialogBody,
  FirmDocumentUploadDialogContent,
  FirmDocumentUploadDialogDropzoneClip,
  FirmDocumentUploadDialogDropzoneFrame,
  FirmDocumentUploadDialogHelperText,
  FirmDocumentUploadDialogProgress,
  FirmDocumentUploadDialogRejectedFile,
  FirmDocumentUploadDialogRejectedFileContent,
  FirmDocumentUploadDialogRejectedFileIcon,
  FirmDocumentUploadDialogRejectedFileName,
  FirmDocumentUploadDialogRejectedFileReason,
  FirmDocumentUploadDialogRemoveFileButton,
  FirmDocumentUploadDialogSelectedFileContent,
  FirmDocumentUploadDialogSelectedFileIcon,
  FirmDocumentUploadDialogSelectedFileMeta,
  FirmDocumentUploadDialogSelectedFileName,
  FirmDocumentUploadDialogSelectedFileRow,
  FirmDocumentUploadDialogSelectedFiles,
  FirmDocumentUploadDialogSelectedFilesFrame,
  FirmDocumentUploadDialogSelectGrid,
  FirmDocumentUploadDialogStatusSlot,
  FirmDocumentUploadDialogTotalProgress,
  FirmDocumentUploadDialogTotalProgressBar,
  FirmDocumentUploadDialogTotalProgressHeader,
} from './FirmDocumentUploadDialog.styles';
import { FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS } from './FirmDocumentUploadDialog.testIds';
import type { FirmDocumentUploadDialogProps } from './FirmDocumentUploadDialog.types';

interface RejectedUploadFile {
  file: File;
  reasons: string[];
}

interface SelectedUploadFileRowProps {
  file: File;
  fileProgressValue: number | null;
  fileProgressVariant: 'determinate' | 'indeterminate' | null;
  hasConflict: boolean;
  index: number;
  isLocked: boolean;
  onRemove: (fileKey: string) => void;
  statusLabel: string;
}

interface RejectedUploadFileRowProps {
  file: File;
  index: number;
  onRemove: (fileKey: string) => void;
  reasons: string[];
}

const FIRM_DOCUMENT_UPLOAD_ACCEPT = validFileTypes.map(fileType => `.${fileType}`).join(',');
const FIRM_DOCUMENT_UPLOAD_SUPPORTED_FORMATS = validFileTypes
  .map(fileType => fileType.toUpperCase())
  .sort((firstFileType, secondFileType) => firstFileType.localeCompare(secondFileType))
  .join(', ');
const FIRM_DOCUMENT_UPLOAD_FILE_ROW_SELECTOR = '[data-firm-document-upload-file-row]';
const FIRM_DOCUMENT_UPLOAD_SCROLL_TOLERANCE_PX = 1;
const DUPLICATE_DOCUMENT_NAME_ERROR = 'A document with this name already exists in this location.';

const getMeasurementDateLabel = ({ date, name }: { date: string; name: string }) => shortDate(date) ?? name;
const getFileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() ?? '';
const getFileKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;
const normalizeFileName = (fileName: string) => fileName.trim().toLowerCase();

const formatFileSize = (fileSize: number) => {
  const sizeInMb = fileSize / 1024 / 1024;

  if (sizeInMb >= 1) {
    return `${sizeInMb.toFixed(sizeInMb >= 10 ? 0 : 1)} MB`;
  }

  return `${Math.max(1, Math.round(fileSize / 1024))} KB`;
};

const formatRejectedFileReasons = (reasons: string[]) => {
  const [firstReason, ...remainingReasons] = reasons;

  if (!firstReason) {
    return '';
  }

  if (!remainingReasons.length) {
    return `${firstReason.charAt(0).toUpperCase()}${firstReason.slice(1)}.`;
  }

  const joinedReasons = remainingReasons.length === 1
    ? `${firstReason} and ${remainingReasons[0]}`
    : [firstReason, ...remainingReasons.slice(0, -1), `and ${remainingReasons.at(-1)}`].join(', ');

  return `${joinedReasons.charAt(0).toUpperCase()}${joinedReasons.slice(1)}.`;
};

const getRejectedUploadFile = (file: File): RejectedUploadFile | null => {
  const fileExtension = getFileExtension(file.name);
  const reasons: string[] = [];

  if (file.size > MAX_DOCUMENT_FILE_SIZE_ALLOWED) {
    reasons.push(`exceeds ${MAX_DOCUMENT_SIZE_IN_MB} MB`);
  }

  if (!validFileTypes.includes(fileExtension)) {
    reasons.push(`${fileExtension ? fileExtension.toUpperCase() : 'file type'} is not supported`);
  }

  if (reasons.length) {
    return { file, reasons };
  }

  return null;
};

const SelectedUploadFileRow = ({
  file,
  fileProgressValue,
  fileProgressVariant,
  hasConflict,
  index,
  isLocked,
  onRemove,
  statusLabel,
}: SelectedUploadFileRowProps) => {
  const fileNameRef = useRef<HTMLSpanElement | null>(null);
  const fileExtension = getFileExtension(file.name);
  const fileKey = getFileKey(file);
  const shouldShowProgress = fileProgressVariant !== null;

  return (
    <FirmDocumentUploadDialogSelectedFileRow
      data-firm-document-upload-file-row
      data-testid={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.selectedFile(index)}
      hasConflict={hasConflict}>
      <FirmDocumentUploadDialogSelectedFileIcon>
        <FileTypeIcon fileType={fileExtension} />
      </FirmDocumentUploadDialogSelectedFileIcon>
      <FirmDocumentUploadDialogSelectedFileContent>
        <OverflowTooltip measureRef={fileNameRef} placement="bottom" title={file.name}>
          <FirmDocumentUploadDialogSelectedFileName ref={fileNameRef}>
            {file.name}
          </FirmDocumentUploadDialogSelectedFileName>
        </OverflowTooltip>
        <FirmDocumentUploadDialogSelectedFileMeta>
          {statusLabel}
        </FirmDocumentUploadDialogSelectedFileMeta>
      </FirmDocumentUploadDialogSelectedFileContent>
      <FirmDocumentUploadDialogRemoveFileButton
        aria-label={`Remove ${file.name}`}
        data-testid={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.removeSelectedFile(index)}
        disabled={isLocked}
        onClick={() => onRemove(fileKey)}
        type="button">
        <CloseRoundedIcon />
      </FirmDocumentUploadDialogRemoveFileButton>
      {shouldShowProgress ? (
        <FirmDocumentUploadDialogProgress
          value={fileProgressVariant === 'determinate' ? fileProgressValue ?? 0 : undefined}
          variant={fileProgressVariant}
        />
      ) : null}
    </FirmDocumentUploadDialogSelectedFileRow>
  );
};

const RejectedUploadFileRow = ({ file, index, onRemove, reasons }: RejectedUploadFileRowProps) => {
  const fileNameRef = useRef<HTMLSpanElement | null>(null);
  const fileExtension = getFileExtension(file.name);
  const fileKey = getFileKey(file);

  return (
    <FirmDocumentUploadDialogRejectedFile
      role="alert"
      data-firm-document-upload-file-row
      data-testid={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.rejectedFile(index)}>
      <FirmDocumentUploadDialogRejectedFileIcon>
        <FileTypeIcon fileType={fileExtension} />
      </FirmDocumentUploadDialogRejectedFileIcon>
      <FirmDocumentUploadDialogRejectedFileContent>
        <OverflowTooltip measureRef={fileNameRef} placement="bottom" title={file.name}>
          <FirmDocumentUploadDialogRejectedFileName ref={fileNameRef}>
            {file.name}
          </FirmDocumentUploadDialogRejectedFileName>
        </OverflowTooltip>
        <FirmDocumentUploadDialogRejectedFileReason>
          {formatRejectedFileReasons(reasons)}
        </FirmDocumentUploadDialogRejectedFileReason>
      </FirmDocumentUploadDialogRejectedFileContent>
      <FirmDocumentUploadDialogRemoveFileButton
        aria-label={`Dismiss ${file.name}`}
        onClick={() => onRemove(fileKey)}
        type="button">
        <CloseRoundedIcon />
      </FirmDocumentUploadDialogRemoveFileButton>
    </FirmDocumentUploadDialogRejectedFile>
  );
};

const getSelectedFileStatus = ({
  file,
  hasConflict,
  index,
  isUploading,
  uploadProgress,
}: {
  file: File;
  hasConflict: boolean;
  index: number;
  isUploading: boolean;
  uploadProgress: FirmDocumentUploadDialogProps['uploadProgress'];
}) => {
  if (!isUploading && hasConflict) {
    return {
      fileProgressValue: null,
      fileProgressVariant: null,
      statusLabel: DUPLICATE_DOCUMENT_NAME_ERROR,
    };
  }

  if (!isUploading || !uploadProgress) {
    return {
      fileProgressValue: null,
      fileProgressVariant: null,
      statusLabel: `Ready to upload • ${formatFileSize(file.size)}`,
    };
  }

  if (index < uploadProgress.completedFiles) {
    return {
      fileProgressValue: 100,
      fileProgressVariant: 'determinate' as const,
      statusLabel: `Uploaded • ${formatFileSize(file.size)}`,
    };
  }

  if (index === uploadProgress.currentFileIndex) {
    return {
      fileProgressValue: null,
      fileProgressVariant: 'indeterminate' as const,
      statusLabel: `Uploading ${index + 1} of ${uploadProgress.totalFiles} • ${formatFileSize(file.size)}`,
    };
  }

  return {
    fileProgressValue: null,
    fileProgressVariant: null,
    statusLabel: `Waiting • ${formatFileSize(file.size)}`,
  };
};

const getTotalProgressValue = (uploadProgress: FirmDocumentUploadDialogProps['uploadProgress']) => {
  if (!uploadProgress?.totalFiles) {
    return 0;
  }

  return Math.min(
    100,
    Math.round((uploadProgress.completedFiles / uploadProgress.totalFiles) * 100)
  );
};

const getMeasurementDateDocumentsId = (
  documents: ReturnType<typeof useFirmDocumentMeasurementDateDocumentsQuery>['data']
) => (
  documents?.id
    ?? documents?.files?.find(documentReference => documentReference.md_documents)?.md_documents
    ?? documents?.folders.find(folder => folder.md_documents)?.md_documents
    ?? null
);

const FirmDocumentUploadDialog = ({
  autoCloseSeconds = null,
  companyOptions,
  firmId = null,
  firmLevelDestination = null,
  initialCompanyId = null,
  initialFolderId = null,
  initialMeasurementDateDate = null,
  initialMeasurementDateId = null,
  isCompanyLocked = false,
  isUploadComplete = false,
  isUploading = false,
  onClose,
  onConfirm,
  open,
  uploadProgress = null,
}: FirmDocumentUploadDialogProps) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(initialCompanyId);
  const [selectedMeasurementDateId, setSelectedMeasurementDateId] = useState<number | null>(initialMeasurementDateId);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(initialFolderId);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<RejectedUploadFile[]>([]);
  const [isFileListScrollable, setIsFileListScrollable] = useState(false);
  const selectedFilesListRef = useRef<HTMLDivElement | null>(null);
  const isFirmLevel = Boolean(firmLevelDestination);
  const shouldShowCompanySelect = !isCompanyLocked && !isFirmLevel;
  let selectColumnCount = 2;
  if (isFirmLevel) {
    selectColumnCount = 1;
  } else if (shouldShowCompanySelect) {
    selectColumnCount = 3;
  }
  const companyMeasurementDatesQuery = useGetMeasurementDatesByCompany({
    companyId: selectedCompanyId ?? undefined,
    shouldQueryAutomatically: Boolean(open && selectedCompanyId),
  });
  const measurementDates = useMemo(
    () => companyMeasurementDatesQuery.data ?? [],
    [companyMeasurementDatesQuery.data]
  );
  const firmDocumentFoldersQuery = useFirmDocumentFoldersQuery({
    companyId: selectedCompanyId,
    enabled: Boolean(open && selectedCompanyId && selectedMeasurementDateId),
    firmId,
    measurementDateId: selectedMeasurementDateId,
  });
  const measurementDateDocumentsQuery = useFirmDocumentMeasurementDateDocumentsQuery({
    companyId: selectedCompanyId,
    enabled: Boolean(open && selectedCompanyId && selectedMeasurementDateId),
    measurementDateId: selectedMeasurementDateId,
  });
  const mdDocumentsId = isFirmLevel
    ? FIRM_LEVEL_DOCUMENTS_ID
    : getMeasurementDateDocumentsId(measurementDateDocumentsQuery.data);
  const companySelectOptions = useMemo<FirmDocumentDialogSearchableSelectOption[]>(
    () => companyOptions.map(company => ({
      id: company.id,
      label: company.name,
    })),
    [companyOptions]
  );
  const measurementDateSelectOptions = useMemo<FirmDocumentDialogSearchableSelectOption[]>(
    () => measurementDates
      .filter((measurementDate): measurementDate is typeof measurementDate & { id: number } =>
        typeof measurementDate.id === 'number'
      )
      .map(measurementDate => ({
        id: measurementDate.id,
        label: getMeasurementDateLabel(measurementDate),
        searchText: `${measurementDate.name} ${measurementDate.date}`,
      })),
    [measurementDates]
  );
  const folderOptions = useMemo<FirmDocumentDialogSearchableSelectOption[]>(
    () => firmLevelDestination?.folderOptions ?? [
      { id: null, label: 'No Folder' },
      ...(firmDocumentFoldersQuery.data?.folders ?? []).map(folder => ({
        id: folder.id,
        label: folder.name,
      })),
    ],
    [firmDocumentFoldersQuery.data?.folders, firmLevelDestination?.folderOptions]
  );
  const selectedCompany = companyOptions.find(company => company.id === selectedCompanyId);
  const selectedMeasurementDate = measurementDates.find(measurementDate => measurementDate.id === selectedMeasurementDateId);
  const existingFileNamesInDestination = useMemo(() => new Set(
    firmLevelDestination
      ? (firmLevelDestination.existingFileNamesByFolderId[String(selectedFolderId)] ?? []).map(normalizeFileName)
      : (measurementDateDocumentsQuery.data?.files ?? [])
        .filter(documentReference => (documentReference.folder ?? null) === selectedFolderId)
        .map(documentReference => normalizeFileName(getFirmDocumentReferenceFileName(documentReference)))
  ), [firmLevelDestination, measurementDateDocumentsQuery.data?.files, selectedFolderId]);
  const selectedFileNameCounts = useMemo(
    () => selectedFiles.reduce<Record<string, number>>((accumulator, file) => {
      const normalizedFileName = normalizeFileName(file.name);

      accumulator[normalizedFileName] = (accumulator[normalizedFileName] ?? 0) + 1;

      return accumulator;
    }, {}),
    [selectedFiles]
  );
  const isUploadFlowLocked = isUploading || isUploadComplete;
  // Refetched destination data includes newly uploaded files, so duplicate checks must stay pre-submit only.
  const shouldEvaluateFileNameConflicts = !isUploadFlowLocked;
  const selectedFilesWithConflicts = useMemo(
    () => selectedFiles.map(file => {
      if (!shouldEvaluateFileNameConflicts) {
        return { file, hasConflict: false };
      }

      const normalizedFileName = normalizeFileName(file.name);

      return {
        file,
        hasConflict: existingFileNamesInDestination.has(normalizedFileName)
          || (selectedFileNameCounts[normalizedFileName] ?? 0) > 1,
      };
    }),
    [existingFileNamesInDestination, selectedFileNameCounts, selectedFiles, shouldEvaluateFileNameConflicts]
  );
  const hasFileNameConflict = selectedFilesWithConflicts.some(({ hasConflict }) => hasConflict);
  const totalProgressValue = getTotalProgressValue(uploadProgress);
  const shouldShowFileList = selectedFiles.length > 0 || rejectedFiles.length > 0;
  const uploadStatusText = !mdDocumentsId && selectedCompany && selectedMeasurementDate
    ? 'Loading the selected destination...'
    : null;
  const initialDateStatusText = initialMeasurementDateDate
    && selectedCompany
    && !selectedMeasurementDate
    && !companyMeasurementDatesQuery.isLoading
    ? 'This company does not have the originally selected date. Choose another measurement date.'
    : null;
  const { hiddenItemCounts, scrollToHiddenItems, updateHiddenItemCounts } = useScrollHiddenItemCounts<HTMLDivElement>({
    isEnabled: shouldShowFileList,
    itemSelector: FIRM_DOCUMENT_UPLOAD_FILE_ROW_SELECTOR,
    scrollContainerRef: selectedFilesListRef,
  });
  const canUpload = Boolean(
    selectedFiles.length > 0
      && (isFirmLevel || (selectedCompanyId && selectedMeasurementDateId && mdDocumentsId))
      && !hasFileNameConflict
      && !isUploadFlowLocked
  );

  const handleFilesChange = useCallback((files: File[]) => {
    const nextRejectedFiles: RejectedUploadFile[] = [];
    const validFiles: File[] = [];

    files.forEach(file => {
      const rejectedFile = getRejectedUploadFile(file);

      if (rejectedFile) {
        nextRejectedFiles.push(rejectedFile);
        return;
      }

      validFiles.push(file);
    });

    setRejectedFiles(currentFiles => {
      const currentFileKeys = new Set(currentFiles.map(({ file }) => getFileKey(file)));
      const nextFiles = [...currentFiles];

      nextRejectedFiles.forEach(rejectedFile => {
        const fileKey = getFileKey(rejectedFile.file);

        if (!currentFileKeys.has(fileKey)) {
          currentFileKeys.add(fileKey);
          nextFiles.push(rejectedFile);
        }
      });

      return nextFiles;
    });
    setSelectedFiles(currentFiles => {
      const currentFileKeys = new Set(currentFiles.map(getFileKey));
      const nextFiles = [...currentFiles];

      validFiles.forEach(file => {
        const fileKey = getFileKey(file);

        if (!currentFileKeys.has(fileKey)) {
          currentFileKeys.add(fileKey);
          nextFiles.push(file);
        }
      });

      return nextFiles;
    });
  }, []);

  const handleRemoveFile = useCallback((fileKey: string) => {
    setSelectedFiles(currentFiles => currentFiles.filter(file => getFileKey(file) !== fileKey));
  }, []);

  const handleRemoveRejectedFile = useCallback((fileKey: string) => {
    setRejectedFiles(currentFiles => currentFiles.filter(({ file }) => getFileKey(file) !== fileKey));
  }, []);

  const handleConfirm = useCallback(() => {
    if (isFirmLevel && selectedFiles.length) {
      onConfirm({
        files: selectedFiles,
        folderId: selectedFolderId,
        mdDocumentsId: FIRM_LEVEL_DOCUMENTS_ID,
        measurementDateDate: FIRM_LEVEL_DOCUMENTS_QUERY_VALUE,
        measurementDateId: FIRM_LEVEL_DOCUMENTS_ID,
      });
      return;
    }
    if (!selectedFiles.length || !selectedMeasurementDateId || !selectedMeasurementDate || !mdDocumentsId) {
      return;
    }

    onConfirm({
      files: selectedFiles,
      folderId: selectedFolderId,
      mdDocumentsId,
      measurementDateDate: selectedMeasurementDate.date,
      measurementDateId: selectedMeasurementDateId,
    });
  }, [
    isFirmLevel,
    mdDocumentsId,
    onConfirm,
    selectedFiles,
    selectedFolderId,
    selectedMeasurementDate,
    selectedMeasurementDateId,
  ]);

  // Reset upload target state to the row context each time the modal opens.
  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedFiles([]);
    setRejectedFiles([]);
    setSelectedCompanyId(initialCompanyId);
    setSelectedMeasurementDateId(initialMeasurementDateId);
    setSelectedFolderId(initialFolderId);
  }, [initialCompanyId, initialFolderId, initialMeasurementDateId, open]);

  // When All Documents opens upload from a visible date, default the selected company to its matching MD row once.
  useEffect(() => {
    if (!open || !initialMeasurementDateDate || selectedMeasurementDateId !== null || !measurementDates.length) {
      return;
    }

    const matchingMeasurementDate = measurementDates.find(
      measurementDate => measurementDate.date === initialMeasurementDateDate
    );

    setSelectedMeasurementDateId(matchingMeasurementDate?.id ?? null);
  }, [initialMeasurementDateDate, measurementDates, open, selectedMeasurementDateId]);

  // Clear folder selection when changing company or measurement date invalidates the previous folder target.
  useEffect(() => {
    if (!open || selectedFolderId === null) {
      return;
    }

    if (!folderOptions.some(option => option.id === selectedFolderId)) {
      setSelectedFolderId(null);
    }
  }, [folderOptions, open, selectedFolderId]);

  // Keep the upload-list gutter tied to overflow, not scroll position, so rows do not resize while scrolling.
  useLayoutEffect(() => {
    if (!shouldShowFileList) {
      setIsFileListScrollable(false);
      return undefined;
    }

    const fileListElement = selectedFilesListRef.current;

    if (!fileListElement) {
      return undefined;
    }

    const updateFileListScrollableState = () => {
      const nextIsScrollable = (
        fileListElement.scrollHeight > fileListElement.clientHeight + FIRM_DOCUMENT_UPLOAD_SCROLL_TOLERANCE_PX
      );

      setIsFileListScrollable(currentIsScrollable => (
        currentIsScrollable === nextIsScrollable ? currentIsScrollable : nextIsScrollable
      ));
    };
    const ownerWindow = fileListElement.ownerDocument.defaultView ?? window;
    const ResizeObserverConstructor = ownerWindow.ResizeObserver;
    const MutationObserverConstructor = ownerWindow.MutationObserver;
    const resizeObserver = ResizeObserverConstructor
      ? new ResizeObserverConstructor(updateFileListScrollableState)
      : null;
    const mutationObserver = MutationObserverConstructor
      ? new MutationObserverConstructor(updateFileListScrollableState)
      : null;

    updateFileListScrollableState();
    resizeObserver?.observe(fileListElement);
    mutationObserver?.observe(fileListElement, { childList: true, subtree: true });
    ownerWindow.addEventListener('resize', updateFileListScrollableState);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      ownerWindow.removeEventListener('resize', updateFileListScrollableState);
    };
  }, [rejectedFiles.length, selectedFiles.length, shouldShowFileList]);

  return (
    <Dialog
      data-testid={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.dialog}
      disableEscapeKeyDown={isUploading}
      fullWidth
      maxWidth="md"
      onClose={isUploading ? undefined : onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogCloseButton
          aria-label="Close upload document dialog"
          data-testid={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.closeButton}
          disabled={isUploading}
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <FirmDocumentUploadDialogBody>
        <FirmDocumentUploadDialogContent>
          <FirmDocumentUploadDialogSelectGrid columnCount={selectColumnCount}>
            {shouldShowCompanySelect ? (
              <FirmDocumentDialogSearchableSelect
                clearSearchAriaLabel="Clear company search"
                disabled={isUploading || companySelectOptions.length === 0}
                emptyLabel="No companies found"
                itemLabelPlural="companies"
                itemLabelSingular="company"
                label="Company"
                menuTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.companyMenu}
                onChange={companyId => {
                  setSelectedCompanyId(companyId);
                  setSelectedMeasurementDateId(null);
                  setSelectedFolderId(null);
                }}
                options={companySelectOptions}
                placeholder="Select Company"
                searchPlaceholder="Find a Company"
                searchTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.companySearch}
                selectTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.companySelect}
                selectedId={selectedCompanyId}
                testIdForOption={id => FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.companyOption(id ?? 0)}
              />
            ) : null}
            {isFirmLevel ? null : (
              <FirmDocumentDialogSearchableSelect
                clearSearchAriaLabel="Clear measurement date search"
                disabled={isUploading || !selectedCompany}
                emptyLabel={companyMeasurementDatesQuery.isLoading
                  ? 'Loading measurement dates...'
                  : 'No measurement dates found'}
                isLoading={companyMeasurementDatesQuery.isLoading}
                itemLabelPlural="measurement dates"
                itemLabelSingular="measurement date"
                label="Measurement Date"
                menuTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.measurementDateMenu}
                onChange={measurementDateId => {
                  setSelectedMeasurementDateId(measurementDateId);
                  setSelectedFolderId(null);
                }}
                options={measurementDateSelectOptions}
                placeholder="Select Measurement Date"
                searchPlaceholder="Find a Measurement Date"
                searchTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.measurementDateSearch}
                selectTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.measurementDateSelect}
                selectedId={selectedMeasurementDateId}
                testIdForOption={id => FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.measurementDateOption(id ?? 0)}
              />
            )}
            <FirmDocumentDialogSearchableSelect
              clearSearchAriaLabel="Clear folder search"
              disabled={isUploading || (!isFirmLevel && !selectedMeasurementDate)}
              emptyLabel={firmDocumentFoldersQuery.isLoading ? 'Loading folders...' : 'No folders found'}
              isLoading={firmDocumentFoldersQuery.isLoading}
              itemLabelPlural="folders"
              itemLabelSingular="folder"
              label="Folder"
              menuTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.folderMenu}
              onChange={setSelectedFolderId}
              options={folderOptions}
              placeholder="Select Folder"
              searchPlaceholder="Find a Folder"
              searchTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.folderSearch}
              selectTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.folderSelect}
              selectedId={selectedFolderId}
              testIdForOption={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.folderOption}
            />
          </FirmDocumentUploadDialogSelectGrid>
          <FirmDocumentUploadDialogDropzoneFrame isHidden={isUploadFlowLocked}>
            <FirmDocumentUploadDialogDropzoneClip isHidden={isUploadFlowLocked}>
              <FileUploadDropzone
                accept={FIRM_DOCUMENT_UPLOAD_ACCEPT}
                disabled={isUploadFlowLocked}
                helperText={(
                  <>
                    <span>{MAX_DOCUMENT_SIZE_IN_MB} MB max per file.</span>
                    <span>Supported formats: {FIRM_DOCUMENT_UPLOAD_SUPPORTED_FORMATS}.</span>
                  </>
                )}
                inputTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.fileInput}
                multiple
                onFilesChange={handleFilesChange}
                primaryText="Drag & Drop Documents"
                secondaryText={null}
                supportingText="or Select Files"
                testId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.dropzone}
              />
            </FirmDocumentUploadDialogDropzoneClip>
          </FirmDocumentUploadDialogDropzoneFrame>
          {(isUploading || isUploadComplete) && uploadProgress ? (
            <FirmDocumentUploadDialogTotalProgress data-testid={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.totalProgress}>
              <FirmDocumentUploadDialogTotalProgressHeader>
                <span>
                  {isUploadComplete ? 'Uploaded' : 'Uploading'}
                  {' '}
                  {isUploadComplete
                    ? uploadProgress.completedFiles
                    : Math.min(uploadProgress.completedFiles + 1, uploadProgress.totalFiles)}
                  {' '}
                  of {uploadProgress.totalFiles}
                </span>
                <span>{totalProgressValue}%</span>
              </FirmDocumentUploadDialogTotalProgressHeader>
              <FirmDocumentUploadDialogTotalProgressBar
                value={totalProgressValue}
                variant="determinate"
              />
            </FirmDocumentUploadDialogTotalProgress>
          ) : null}
          {shouldShowFileList ? (
            <FirmDocumentUploadDialogSelectedFilesFrame>
              {hiddenItemCounts.above > 0 ? (
                <ScrollHiddenItemsHint
                  count={hiddenItemCounts.above}
                  itemLabelPlural="files"
                  itemLabelSingular="file"
                  onClick={() => scrollToHiddenItems('above')}
                  placement="top"
                />
              ) : null}
              <FirmDocumentUploadDialogSelectedFiles
                ref={selectedFilesListRef}
                hasScrollGutter={isFileListScrollable}
                onScroll={updateHiddenItemCounts}>
                {selectedFilesWithConflicts.map(({ file, hasConflict }, index) => {
                  const { fileProgressValue, fileProgressVariant, statusLabel } = getSelectedFileStatus({
                    file,
                    hasConflict,
                    index,
                    isUploading,
                    uploadProgress,
                  });

                  return (
                    <SelectedUploadFileRow
                      key={getFileKey(file)}
                      file={file}
                      fileProgressValue={fileProgressValue}
                      fileProgressVariant={fileProgressVariant}
                      hasConflict={hasConflict}
                      index={index}
                      isLocked={isUploadFlowLocked}
                      onRemove={handleRemoveFile}
                      statusLabel={statusLabel}
                    />
                  );
                })}
                {rejectedFiles.map(({ file, reasons }, index) => (
                  <RejectedUploadFileRow
                    key={getFileKey(file)}
                    file={file}
                    index={index}
                    onRemove={handleRemoveRejectedFile}
                    reasons={reasons}
                  />
                ))}
              </FirmDocumentUploadDialogSelectedFiles>
              {hiddenItemCounts.below > 0 ? (
                <ScrollHiddenItemsHint
                  count={hiddenItemCounts.below}
                  itemLabelPlural="files"
                  itemLabelSingular="file"
                  onClick={() => scrollToHiddenItems('below')}
                  placement="bottom"
                />
              ) : null}
            </FirmDocumentUploadDialogSelectedFilesFrame>
          ) : null}
          <FirmDocumentUploadDialogStatusSlot aria-live="polite">
            {uploadStatusText || initialDateStatusText ? (
              <FirmDocumentUploadDialogHelperText>
                {uploadStatusText ?? initialDateStatusText}
              </FirmDocumentUploadDialogHelperText>
            ) : null}
          </FirmDocumentUploadDialogStatusSlot>
        </FirmDocumentUploadDialogContent>
      </FirmDocumentUploadDialogBody>
      {isUploadComplete ? (
        <DialogActions>
          <Button
            data-testid={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.cancelButton}
            disabled={isUploading}
            onClick={onClose}
            visualVariant="primaryGhost">
            Close{autoCloseSeconds !== null && autoCloseSeconds > 0 ? ` in ${autoCloseSeconds}s` : ''}
          </Button>
        </DialogActions>
      ) : (
        <DialogActionFooter
          actionButtonTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.uploadButton}
          actionLabel={selectedFiles.length === 1 ? 'Upload Document' : 'Upload Documents'}
          cancelButtonTestId={FIRM_DOCUMENT_UPLOAD_DIALOG_TEST_IDS.cancelButton}
          isActionDisabled={!canUpload}
          isActionLoading={isUploading}
          isCancelDisabled={isUploading}
          onAction={handleConfirm}
          onCancel={onClose}
        />
      )}
    </Dialog>
  );
};

export default FirmDocumentUploadDialog;
